import {Button, DataStatus} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {PartialVehiclePhotosRoutes} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, useOnMount} from 'shared/web';

import {useBatchDownload} from '../../hooks/useBatchDownload';
import {DownloadDialog} from './components/DownloadDialog';
import {ServiceCasePhotosCardContainer} from './components/ServiceCasePhotosCardContainer';
import {useHistoricalServiceCaseAlbums} from './hooks/useHistoricalServiceCaseAlbums';

interface ServiceCasePhotosProps {
  vehicleId: string;
  ignoredServiceCaseId?: string | Nullish;
  /** Hide the whole section behind a "Show previous service case photos" button (ServiceCaseDetail usage). */
  behindButton?: boolean;
}

export function ServiceCasePhotos(props: ServiceCasePhotosProps) {
  const {handleDownload, eventData, isProgressVisible, dismissProgress} = useBatchDownload();

  const {
    albumGroups,
    isLoading: isAlbumsLoading,
    isError,
    load,
  } = useHistoricalServiceCaseAlbums({
    vehicleId: props.vehicleId,
    ignoredServiceCaseId: props.ignoredServiceCaseId,
  });

  // VehicleDetail fetches immediately; ServiceCaseDetail waits for the reveal button.
  const [isRevealed, setIsRevealed] = useState(!props.behindButton);

  useOnMount(() => {
    if (!props.behindButton) {
      void load();
    }
  });

  const handleReveal = () => {
    setIsRevealed(true);
    void load();
  };

  const handleVehicleAlbumBatchDownload = (albumId: string) =>
    handleDownload({
      vehiclePhotoLocation: PartialVehiclePhotosRoutes.vehicle,
      vehicleId: props.vehicleId,
      albumId,
    });

  const handleInspectionGalleryBatchDownload = (auditId: string | Nullish) =>
    handleDownload({
      vehiclePhotoLocation: PartialVehiclePhotosRoutes.inspections,
      vehicleId: props.vehicleId,
      auditId: auditId ?? undefined,
    });

  if (!isRevealed) {
    return (
      <HStack justify="center">
        <Button
          variant="link"
          title={i18n.t('entity.workshop.actions.showPreviousServiceCasePhotos')}
          onClick={handleReveal}
          data-testid="serviceCasePhotos-showPrevious"
        />
      </HStack>
    );
  }

  return (
    <>
      <DataStatus
        isLoading={isAlbumsLoading || (isNil(albumGroups) && !isError)}
        isError={isError}
        isEmpty={isNotNil(albumGroups) && isEmpty(albumGroups)}
        emptyMessage={i18n.t('entity.workshop.labels.noPreviousServiceCasePhotos')}
      >
        <VStack spacing={4}>
          {(albumGroups ?? []).map((albumGroup) => (
            <ServiceCasePhotosCardContainer
              key={albumGroup.serviceCaseId}
              data-testid={`serviceCasePhotos-${albumGroup.serviceCaseId}`}
              vehicleId={props.vehicleId}
              serviceCaseId={albumGroup.serviceCaseId}
              albums={albumGroup.albums}
              auditData={albumGroup.auditData}
              onVehicleAlbumDownload={handleVehicleAlbumBatchDownload}
              onInspectionDownload={handleInspectionGalleryBatchDownload}
            />
          ))}
        </VStack>
      </DataStatus>
      <DownloadDialog
        withWrapper
        isOpen={isProgressVisible}
        onClose={dismissProgress}
        fileName={eventData?.data?.bulkName}
        fileCount={eventData?.data?.objects.length}
      />
    </>
  );
}
